import { parseDashboard, VALUE_DIMENSION, buildViewReadQuery, buildMetadataQuery, buildTimeseriesQuery, suggestBucketSeconds } from './chunk-HSEYA6OM.js';
import { RiskLevel } from './chunk-6B5KO6TH.js';
import { normalizeAddress, isValidAddress, shortenAddress, formatGraphAmount, safeTicker } from './chunk-NHT4QHRP.js';
import { toCanonicalChainId } from './chunk-HKIGNAVO.js';

// src/pools/constants.ts
var POOL_FACETS = {
  /** The pool's own contract (V2 pair / V3 pool). */
  poolAddress: "pool_address",
  /** The V4 pool key — an id inside the singleton `PoolManager`, not an address. */
  poolId: "pool_id",
  token0: "token0",
  token1: "token1",
  /** V3/V4 only: USDC/WETH is several pools, one per tier. */
  feeTier: "fee_tier",
  /** Who called the factory. Authored, not read off the mint event. */
  poolCreator: "pool_creator",
  /** Who added or removed liquidity. */
  lpProvider: "lp_provider",
  /** `add` / `remove` — stated by the definition, see {@link LIQUIDITY_ACTIONS}. */
  liquidityAction: "liquidity_action",
  /** The two token legs of a move. The cross-version comparable. */
  amount0: "amount0",
  amount1: "amount1",
  /** V3 `L` units / V4 `liquidityDelta` — the protocol's own statement, not summable across pools. */
  liquidityDelta: "liquidity_delta",
  /** A cross-transaction finding from the context store (see {@link POOL_ALERT_KINDS}). */
  liquidityAlert: "liquidity_alert"
};
var POOL_INVENTORY_FACETS = [
  POOL_FACETS.poolAddress,
  POOL_FACETS.token0,
  POOL_FACETS.token1,
  POOL_FACETS.feeTier,
  POOL_FACETS.poolCreator
];
var POOL_LIQUIDITY_FACETS = [
  POOL_FACETS.poolAddress,
  POOL_FACETS.liquidityAction,
  POOL_FACETS.lpProvider,
  POOL_FACETS.amount0,
  POOL_FACETS.amount1,
  POOL_FACETS.liquidityDelta
];
var LIQUIDITY_ACTIONS = ["add", "remove"];
var LIQUIDITY_ACTION_LABELS = {
  add: "Added",
  remove: "Removed"
};
var POOL_ALERT_KINDS = ["pool_opened", "liquidity_front_run", "liquidity_rug"];
var POOL_ALERT_LABELS = {
  pool_opened: "New pool",
  liquidity_front_run: "Same-block add + remove",
  liquidity_rug: "Creator pulled liquidity"
};
var POOL_ALERT_SEVERITY = {
  pool_opened: RiskLevel.SAFE,
  liquidity_front_run: RiskLevel.MEDIUM,
  liquidity_rug: RiskLevel.HIGH
};
var TOKEN_FLOW_FACETS = {
  /** The launch this transaction belongs to — the scoping chip, at tx grain. */
  launchPool: "launch_pool",
  /**
   * Which token the launch launched — stamped beside `launch_pool` by the same
   * lifecycle pass, which is where "which leg is the launch and which is the
   * quote" was decided. Read it rather than re-deciding it from a pair.
   */
  launchToken: "launch_token",
  /**
   * What the launch is priced in — WETH, a stablecoin. Stamped by the same pass as
   * {@link launchToken}, so it is the pipeline's quote decision (the curated token
   * registry's categories), read rather than re-made from a pair here.
   */
  launchQuote: "launch_quote",
  token: "token",
  amount: "amount",
  sender: "sender",
  recipient: "recipient",
  /** Which log it was, so non-transfers can be dropped. */
  eventName: "event_name",
  tokenSymbol: "token_symbol",
  tokenDecimals: "token_decimals",
  /**
   * The address that SIGNED the transaction — how a launch is found from an actor.
   *
   * The transaction's `from`, not a party to any particular transfer, which is
   * exactly why it is the right chip for "which launches has this address taken
   * part in": it names whoever chose to act, once per transaction, whereas
   * `sender` / `recipient` name a router or the pool as often as a person.
   */
  origin: "origin"
};
var TOKEN_FLOW_READ_FACETS = [
  TOKEN_FLOW_FACETS.token,
  TOKEN_FLOW_FACETS.amount,
  TOKEN_FLOW_FACETS.sender,
  TOKEN_FLOW_FACETS.recipient,
  TOKEN_FLOW_FACETS.eventName,
  TOKEN_FLOW_FACETS.tokenSymbol,
  TOKEN_FLOW_FACETS.tokenDecimals
];
var NATIVE_FLOW_FACETS = {
  /** The wallet that signed the transaction. */
  actor: "actor",
  /** Its signed native + wrapped-native delta, in whole ETH, net of gas. */
  netNative: "actor_net_native"
};
var NATIVE_FLOW_READ_FACETS = [
  NATIVE_FLOW_FACETS.actor,
  NATIVE_FLOW_FACETS.netNative
];
var TRANSFER_EVENT = "Transfer";
var MAX_FLOW_LIMIT = 500;
var POOL_VIEW_IDS = {
  /** One row per pool: tokens, fee tier, creator. */
  inventory: "mv_pool_inventory",
  /** Liquidity moves grouped by pool AND direction — never summed into one number. */
  liquidity: "mv_pool_liquidity"
};
var DEFAULT_POOL_LIMIT = 50;

// src/pools/types.ts
function poolKey(pool) {
  const chain = toCanonicalChainId(pool.chainId) ?? pool.chainId.trim();
  if (pool.address) return `${chain}:addr:${normalizeAddress(pool.address, pool.chainId)}`;
  if (pool.poolId) return `${chain}:id:${normalizeAddress(pool.poolId, pool.chainId)}`;
  return `${chain}:unknown`;
}
function poolIdentity(pool) {
  return pool.address ?? pool.poolId;
}
function poolPairLabel(pool) {
  const legs = [pool.token0, pool.token1].filter(Boolean).map(
    (leg) => isValidAddress(leg) ? shortenAddress(leg) : leg
  );
  return legs.length ? legs.join(" / ") : "Unknown pair";
}

// src/pools/format.ts
function formatFeeTier(feeTier2) {
  const tier = typeof feeTier2 === "number" ? feeTier2 : typeof feeTier2 === "string" && feeTier2.trim() !== "" ? Number(feeTier2) : Number.NaN;
  if (!Number.isFinite(tier)) return void 0;
  const pct = tier / 1e4;
  if (pct !== 0 && Math.abs(pct) < 0.01) return `${pct.toFixed(4).replace(/0+$/, "")}%`;
  return `${pct.toFixed(2)}%`;
}
function formatAmounts(move) {
  const legs = [move.amount0, move.amount1].filter(Boolean);
  if (legs.length) return legs.join(" / ");
  return move.liquidityDelta ? `\u0394 ${move.liquidityDelta}` : void 0;
}
function formatLegAmounts(move, tokens) {
  if (!tokens) return formatAmounts(move);
  const leg = (amount, token) => {
    if (!amount) return void 0;
    const decimals2 = token?.decimals;
    if (decimals2 == null) return amount;
    let units;
    try {
      units = BigInt(amount);
    } catch {
      return amount;
    }
    const scaled = formatGraphAmount(formatBaseUnits(units, decimals2, 6), { maxChars: 14 })?.text;
    const symbol = token?.symbol ? safeTicker(token.symbol).text : "";
    return symbol ? `${scaled} ${symbol}` : scaled;
  };
  const legs = [leg(move.amount0, tokens[0]), leg(move.amount1, tokens[1])].filter(Boolean);
  if (legs.length) return legs.join(" / ");
  return move.liquidityDelta ? `\u0394 ${move.liquidityDelta}` : void 0;
}
function formatBaseUnits(value, decimals2, maxFractionDigits = 4) {
  if (decimals2 === void 0 || !Number.isInteger(decimals2) || decimals2 < 0) {
    return value.toString();
  }
  const sign = value < 0n ? "-" : "";
  const magnitude = value < 0n ? -value : value;
  const scale = 10n ** BigInt(decimals2);
  const whole = (magnitude / scale).toString();
  if (decimals2 === 0 || maxFractionDigits <= 0) return `${sign}${whole}`;
  const fraction = (magnitude % scale).toString().padStart(decimals2, "0").slice(0, maxFractionDigits).replace(/0+$/, "");
  return fraction ? `${sign}${whole}.${fraction}` : `${sign}${whole}`;
}

// src/pools/query.ts
function poolChip(pool) {
  return `${POOL_FACETS.poolAddress}:eq:${pool}`;
}
function withPoolChip(filters) {
  const { pool, where, ...rest } = filters;
  if (!pool) return { ...rest, where };
  return { ...rest, where: [...where ?? [], poolChip(pool)] };
}
function buildPoolInventoryQuery(filters = {}) {
  const { pool, limit, scope } = { limit: DEFAULT_POOL_LIMIT, ...filters };
  if (canUseInventoryView(filters)) {
    return buildViewReadQuery(POOL_VIEW_IDS.inventory, {
      filters: pool ? { [POOL_FACETS.poolAddress]: pool } : void 0,
      limit,
      scope
    });
  }
  return buildMetadataQuery({
    ...withPoolChip(filters),
    canonicalNames: [...POOL_INVENTORY_FACETS],
    limit
  });
}
function canUseInventoryView(filters) {
  return filters.since === void 0 && filters.until === void 0 && !filters.where?.length && filters.txId === void 0 && filters.chainId === void 0 && filters.minConfidence === void 0 && filters.definitionScope === void 0;
}
function buildPoolLiquidityQuery(options) {
  const { bucketSeconds, action, limit, ...filters } = options;
  const base = withPoolChip(filters);
  const where = [...base.where ?? []];
  if (action) where.push(`${POOL_FACETS.liquidityAction}:eq:${action}`);
  return buildTimeseriesQuery({
    ...base,
    where: where.length ? where : void 0,
    canonicalNames: [POOL_FACETS.liquidityAction],
    bucketSeconds,
    metric: "count",
    groupBy: "canonical_name",
    limit
  });
}
function buildLpActivityQuery(options = {}) {
  const { action, provider, limit, cursor, ...filters } = options;
  const base = withPoolChip(filters);
  const where = [...base.where ?? []];
  if (action) where.push(`${POOL_FACETS.liquidityAction}:eq:${action}`);
  if (provider) where.push(`${POOL_FACETS.lpProvider}:eq:${provider}`);
  return buildMetadataQuery({
    ...base,
    where: where.length ? where : void 0,
    canonicalNames: [...POOL_LIQUIDITY_FACETS],
    limit: limit ?? DEFAULT_POOL_LIMIT,
    cursor
  });
}
function buildPoolAlertsQuery(options = {}) {
  const { limit, ...filters } = options;
  return buildMetadataQuery({
    ...withPoolChip(filters),
    canonicalNames: [POOL_FACETS.liquidityAlert],
    limit: limit ?? DEFAULT_POOL_LIMIT
  });
}
function buildAddressLaunchesQuery(address, options = {}) {
  const { where, limit, ...rest } = options;
  return buildMetadataQuery({
    ...rest,
    where: [...where ?? [], `${TOKEN_FLOW_FACETS.origin}:eq:${address}`],
    canonicalNames: [TOKEN_FLOW_FACETS.launchPool],
    limit: limit ?? MAX_FLOW_LIMIT
  });
}
function buildLaunchPoolProbeQuery(address, options = {}) {
  return launchFactOfPool(address, TOKEN_FLOW_FACETS.launchPool, options);
}
function buildPoolLaunchTokenQuery(pool, options = {}) {
  return launchFactOfPool(pool, TOKEN_FLOW_FACETS.launchToken, options);
}
function buildPoolLaunchQuoteQuery(pool, options = {}) {
  return launchFactOfPool(pool, TOKEN_FLOW_FACETS.launchQuote, options);
}
function buildTokenLaunchPoolsQuery(token, options = {}) {
  const { where, limit, ...rest } = options;
  return buildMetadataQuery({
    ...rest,
    where: [...where ?? [], `${TOKEN_FLOW_FACETS.launchToken}:eq:${token}`],
    canonicalNames: [TOKEN_FLOW_FACETS.launchPool],
    limit: limit ?? MAX_FLOW_LIMIT
  });
}
function launchFactOfPool(pool, fact, options) {
  const { where, limit: _limit, ...rest } = options;
  return buildMetadataQuery({
    ...rest,
    where: [...where ?? [], `${TOKEN_FLOW_FACETS.launchPool}:eq:${pool}`],
    canonicalNames: [fact],
    limit: 1
  });
}
function buildTokenFlowQuery(options = {}) {
  const { launchPool, token, limit, cursor, where, ...rest } = options;
  const chips = [...where ?? []];
  if (launchPool) chips.push(`${TOKEN_FLOW_FACETS.launchPool}:eq:${launchPool}`);
  if (token) chips.push(`${TOKEN_FLOW_FACETS.token}:eq:${token}`);
  return buildMetadataQuery({
    ...rest,
    where: chips.length ? chips : void 0,
    canonicalNames: [...TOKEN_FLOW_READ_FACETS],
    limit: limit ?? MAX_FLOW_LIMIT,
    cursor
  });
}
function buildNativeFlowQuery(options = {}) {
  const { launchPool, limit, cursor, where, ...rest } = options;
  const chips = [...where ?? []];
  if (launchPool) chips.push(`${TOKEN_FLOW_FACETS.launchPool}:eq:${launchPool}`);
  return buildMetadataQuery({
    ...rest,
    where: chips.length ? chips : void 0,
    canonicalNames: [...NATIVE_FLOW_READ_FACETS],
    limit: limit ?? MAX_FLOW_LIMIT,
    cursor
  });
}

// src/pools/rows.ts
function byEntity(rows) {
  const groups = /* @__PURE__ */ new Map();
  for (const row of rows) {
    if (!row.canonical_name) continue;
    let group = groups.get(row.entity_id);
    if (!group) {
      group = {
        entityId: row.entity_id,
        txId: row.tx_id,
        chainId: row.chain_id,
        timestamp: row.block_timestamp ?? void 0,
        values: /* @__PURE__ */ new Map()
      };
      groups.set(row.entity_id, group);
    }
    if (!group.values.has(row.canonical_name)) {
      group.values.set(row.canonical_name, String(row.value));
    }
  }
  return [...groups.values()];
}
function feeTier(raw) {
  if (raw === void 0) return void 0;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : void 0;
}
function isAction(value) {
  return LIQUIDITY_ACTIONS.includes(value);
}
function isAlertKind(value) {
  return POOL_ALERT_KINDS.includes(value);
}
function poolsFromRows(rows) {
  const pools = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const pool = poolFromViewRow(row);
    if (!pool) continue;
    const key = `${pool.chainId.toLowerCase()}:${(pool.address ?? pool.poolId).toLowerCase()}`;
    if (!pools.has(key)) pools.set(key, pool);
  }
  const creatorByTx = /* @__PURE__ */ new Map();
  const txsByPool = /* @__PURE__ */ new Map();
  for (const group of byEntity(rows)) {
    const address = group.values.get(POOL_FACETS.poolAddress);
    const poolId = group.values.get(POOL_FACETS.poolId);
    if (!address && !poolId) {
      const txCreator = group.values.get(POOL_FACETS.poolCreator);
      if (txCreator && group.txId) creatorByTx.set(group.txId, txCreator);
      continue;
    }
    const key = `${group.chainId.toLowerCase()}:${(address ?? poolId).toLowerCase()}`;
    if (group.txId) {
      const txs = txsByPool.get(key) ?? /* @__PURE__ */ new Set();
      txs.add(group.txId);
      txsByPool.set(key, txs);
    }
    const existing = pools.get(key);
    const token0 = group.values.get(POOL_FACETS.token0);
    const creator = group.values.get(POOL_FACETS.poolCreator);
    const isCreation = token0 !== void 0 || creator !== void 0;
    const merged = {
      chainId: existing?.chainId ?? group.chainId,
      ...address ? { address } : {},
      ...poolId ? { poolId } : {},
      token0: token0 ?? existing?.token0,
      token1: group.values.get(POOL_FACETS.token1) ?? existing?.token1,
      feeTier: feeTier(group.values.get(POOL_FACETS.feeTier)) ?? existing?.feeTier,
      creator: creator ?? existing?.creator,
      createdAt: isCreation ? group.timestamp : existing?.createdAt,
      createdTxId: isCreation ? group.txId : existing?.createdTxId
    };
    const alert = group.values.get(POOL_FACETS.liquidityAlert);
    const alerts = new Set(existing?.alerts ?? []);
    if (isAlertKind(alert)) alerts.add(alert);
    if (alerts.size) merged.alerts = [...alerts];
    pools.set(key, merged);
  }
  for (const [key, pool] of pools) {
    if (pool.creator) continue;
    for (const txId of txsByPool.get(key) ?? []) {
      const creator = creatorByTx.get(txId);
      if (creator) {
        pools.set(key, { ...pool, creator });
        break;
      }
    }
  }
  return [...pools.values()];
}
function lpEventsFromRows(rows) {
  const events = [];
  for (const group of byEntity(rows)) {
    const action = group.values.get(POOL_FACETS.liquidityAction);
    if (!isAction(action)) continue;
    const pool = group.values.get(POOL_FACETS.poolAddress) ?? group.values.get(POOL_FACETS.poolId);
    if (!pool) continue;
    events.push({
      chainId: group.chainId,
      pool,
      action,
      provider: group.values.get(POOL_FACETS.lpProvider),
      amount0: group.values.get(POOL_FACETS.amount0),
      amount1: group.values.get(POOL_FACETS.amount1),
      liquidityDelta: group.values.get(POOL_FACETS.liquidityDelta),
      txId: group.txId,
      timestamp: group.timestamp,
      entityId: group.entityId
    });
  }
  return events;
}
function poolAlertsFromRows(rows) {
  const groups = new Map(byEntity(rows).map((group) => [group.entityId, group]));
  const alerts = [];
  const seen = /* @__PURE__ */ new Set();
  for (const row of rows) {
    if (row.canonical_name !== POOL_FACETS.liquidityAlert) continue;
    const kind = String(row.value);
    if (!isAlertKind(kind)) continue;
    const key = `${row.entity_id}|${kind}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const group = groups.get(row.entity_id);
    const pool = group?.values.get(POOL_FACETS.poolAddress) ?? group?.values.get(POOL_FACETS.poolId) ?? "";
    alerts.push({
      kind,
      chainId: row.chain_id,
      pool,
      txId: row.tx_id,
      timestamp: row.block_timestamp ?? void 0
    });
  }
  return alerts;
}
function logIndexOf(entityId) {
  const match = entityId?.match(/#log#(\d+)$/);
  return match ? Number(match[1]) : void 0;
}
function compareLpEvents(a, b) {
  const byTime = (a.timestamp ?? 0) - (b.timestamp ?? 0);
  if (byTime !== 0) return byTime;
  return (logIndexOf(a.entityId) ?? -1) - (logIndexOf(b.entityId) ?? -1);
}
var MIN_LIQUIDITY_SPAN_SECONDS = 1800;
function liquidityActivityWindow(events) {
  let first = Infinity;
  let last = -Infinity;
  for (const event of events) {
    if (event.timestamp === void 0) continue;
    first = Math.min(first, event.timestamp);
    last = Math.max(last, event.timestamp);
  }
  if (!Number.isFinite(first)) return void 0;
  const pad = Math.max(0, MIN_LIQUIDITY_SPAN_SECONDS - (last - first)) / 2;
  const since = Math.floor(first - pad);
  const until = Math.ceil(last + pad) + 1;
  return { since, until, bucketSeconds: suggestBucketSeconds({ since, until }) };
}
function liquiditySeriesFromEvents(events, bucketSeconds) {
  if (bucketSeconds <= 0) return [];
  const buckets = /* @__PURE__ */ new Map();
  for (const event of events) {
    if (event.timestamp === void 0) continue;
    const bucketStart = Math.floor(event.timestamp / bucketSeconds) * bucketSeconds;
    const point = buckets.get(bucketStart) ?? { bucketStart, added: 0, removed: 0 };
    if (event.action === "add") point.added += 1;
    else point.removed += 1;
    buckets.set(bucketStart, point);
  }
  return [...buckets.values()].sort((a, b) => a.bucketStart - b.bucketStart);
}
function poolFromViewRow(row) {
  const r = row;
  if (r.canonical_name) return null;
  const text = (key) => {
    const v = r[key];
    return typeof v === "string" && v ? v : typeof v === "number" ? String(v) : void 0;
  };
  const address = text(POOL_FACETS.poolAddress);
  const poolId = text(POOL_FACETS.poolId);
  const chainId = text("chain_id");
  if (!address && !poolId || !chainId) return null;
  const pool = { chainId };
  if (address) pool.address = address;
  if (poolId) pool.poolId = poolId;
  const token0 = text(POOL_FACETS.token0);
  const token1 = text(POOL_FACETS.token1);
  const fee = feeTier(text(POOL_FACETS.feeTier));
  const creator = text(POOL_FACETS.poolCreator);
  if (token0) pool.token0 = token0;
  if (token1) pool.token1 = token1;
  if (fee !== void 0) pool.feeTier = fee;
  if (creator) pool.creator = creator;
  return pool;
}
function countPools(rows) {
  return poolsFromRows(rows).length;
}
function decimals(raw) {
  if (raw === void 0) return void 0;
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : void 0;
}
function baseUnits(raw) {
  if (raw === void 0 || !/^-?\d+$/.test(raw)) return void 0;
  return BigInt(raw);
}
function transferLegsFromRows(rows) {
  const legs = [];
  for (const group of byEntity(rows)) {
    if (group.values.get(TOKEN_FLOW_FACETS.eventName) !== TRANSFER_EVENT) continue;
    const token = group.values.get(TOKEN_FLOW_FACETS.token);
    const from = group.values.get(TOKEN_FLOW_FACETS.sender);
    const to = group.values.get(TOKEN_FLOW_FACETS.recipient);
    const amount = group.values.get(TOKEN_FLOW_FACETS.amount);
    if (!token || !from || !to || baseUnits(amount) === void 0) continue;
    legs.push({
      entityId: group.entityId,
      chainId: group.chainId,
      txId: group.txId,
      timestamp: group.timestamp,
      token: token.toLowerCase(),
      amount,
      from: from.toLowerCase(),
      to: to.toLowerCase(),
      symbol: group.values.get(TOKEN_FLOW_FACETS.tokenSymbol),
      decimals: decimals(group.values.get(TOKEN_FLOW_FACETS.tokenDecimals))
    });
  }
  return legs;
}
function netFlowByAddress(legs) {
  const flows = /* @__PURE__ */ new Map();
  const touch = (address, leg) => {
    const key = `${address}:${leg.token}`;
    let flow = flows.get(key);
    if (!flow) {
      flow = {
        address,
        token: leg.token,
        symbol: leg.symbol,
        decimals: leg.decimals,
        in: 0n,
        out: 0n,
        net: 0n,
        legs: 0
      };
      flows.set(key, flow);
    }
    flow.symbol ??= leg.symbol;
    flow.decimals ??= leg.decimals;
    flow.legs += 1;
    return flow;
  };
  for (const leg of legs) {
    const amount = baseUnits(leg.amount);
    if (amount === void 0) continue;
    const sent = touch(leg.from, leg);
    sent.out += amount;
    sent.net -= amount;
    const received = touch(leg.to, leg);
    received.in += amount;
    received.net += amount;
  }
  const magnitude = (value) => value < 0n ? -value : value;
  return [...flows.values()].sort((a, b) => {
    const diff = magnitude(b.net) - magnitude(a.net);
    return diff === 0n ? 0 : diff > 0n ? 1 : -1;
  });
}
function flowForAddress(legs, address) {
  const wanted = address.toLowerCase();
  return netFlowByAddress(legs).filter((flow) => flow.address === wanted);
}
function tokenFlowFromRows(rows, options = {}) {
  const legs = transferLegsFromRows(rows);
  return {
    legs,
    flows: netFlowByAddress(legs),
    truncated: options.truncated ?? false
  };
}
function nativeFlowFromRows(rows) {
  const perTx = /* @__PURE__ */ new Map();
  for (const row of rows) {
    if (!row.canonical_name || !row.tx_id) continue;
    const entry = perTx.get(row.tx_id) ?? {};
    if (row.canonical_name === NATIVE_FLOW_FACETS.actor) {
      entry.address ??= String(row.value).trim().toLowerCase();
    } else if (row.canonical_name === NATIVE_FLOW_FACETS.netNative) {
      const parsed = Number(String(row.value));
      if (Number.isFinite(parsed)) entry.net ??= parsed;
    }
    perTx.set(row.tx_id, entry);
  }
  const flows = /* @__PURE__ */ new Map();
  for (const { address, net } of perTx.values()) {
    if (!address || net === void 0) continue;
    const flow = flows.get(address) ?? { address, net: 0, legs: 0 };
    flow.net += net;
    flow.legs += 1;
    flows.set(address, flow);
  }
  return [...flows.values()].sort((a, b) => b.net - a.net);
}

// src/pools/dashboard.ts
var DEX_POOLS_DASHBOARD = parseDashboard({
  id: "preset_dex_pools",
  name: "DEX pools \u2014 Overview",
  description: "Pools opened, liquidity added and removed, and the LP activity behind it, across the platform's AMMs.",
  scope: "system",
  readonly: true,
  timeRange: { preset: "7d" },
  panels: [
    {
      id: "pools_kpi_count",
      title: "Pools",
      description: "Distinct pools seen in the window.",
      kind: "kpi",
      position: { x: 0, y: 0, w: 3, h: 2 },
      query: {
        source: "timeseries",
        // Distinct VALUES: every liquidity move carries `pool_address` too, so
        // distinct entities counted each add and remove as another pool.
        metric: "count_distinct_value",
        canonicalNames: [POOL_FACETS.poolAddress]
      }
    },
    {
      id: "pools_kpi_moves",
      title: "Liquidity moves",
      description: "Add and remove events, both directions.",
      kind: "kpi",
      position: { x: 3, y: 0, w: 3, h: 2 },
      query: {
        source: "timeseries",
        metric: "count",
        canonicalNames: [POOL_FACETS.liquidityAction]
      }
    },
    {
      id: "pools_ts_liquidity",
      title: "Liquidity activity",
      description: "Add and remove events over time.",
      kind: "timeseries",
      position: { x: 6, y: 0, w: 6, h: 4 },
      query: {
        source: "timeseries",
        metric: "count",
        // Adds versus removes, split by the action's own VALUE. Grouping by
        // `canonical_name` over one fact drew a single "Liquidity action" line
        // under a description promising the split.
        groupBy: VALUE_DIMENSION,
        canonicalNames: [POOL_FACETS.liquidityAction],
        values: [...LIQUIDITY_ACTIONS]
      }
    },
    {
      id: "pools_ts_opened",
      title: "Pools opened",
      description: "Pool creations per bucket.",
      kind: "timeseries",
      position: { x: 0, y: 2, w: 6, h: 4 },
      query: {
        source: "timeseries",
        // A creation's fact: `pool_creator` is authored for every factory call —
        // V4 initialisations included, which state no `token0` — while
        // `pool_address` rides on every add, remove and swap, so counting it drew
        // pool activity under "Pools opened".
        metric: "count_distinct_entity",
        canonicalNames: [POOL_FACETS.poolCreator]
      }
    },
    {
      id: "pools_dist_action",
      title: "Adds vs removes",
      kind: "distribution",
      position: { x: 0, y: 6, w: 4, h: 4 },
      query: { source: "metadata", canonicalNames: [POOL_FACETS.liquidityAction], limit: 500 }
    },
    {
      id: "pools_dist_alerts",
      title: "Liquidity flags",
      description: "Same-block add + remove, and creators pulling liquidity.",
      kind: "distribution",
      position: { x: 4, y: 6, w: 4, h: 4 },
      query: { source: "metadata", canonicalNames: [POOL_FACETS.liquidityAlert], limit: 500 }
    },
    {
      id: "pools_top_providers",
      title: "Most active LPs",
      description: "Ranked by distinct transactions.",
      // Ranked bars, not a donut: a ring of addresses has no reading order, and
      // "most active" is a ranking.
      kind: "toplist",
      position: { x: 8, y: 6, w: 4, h: 4 },
      query: { source: "metadata", canonicalNames: [POOL_FACETS.lpProvider], limit: 500 }
    },
    {
      id: "pools_table_inventory",
      title: "Pools",
      description: "One row per pool: its tokens, fee tier and creator.",
      kind: "table",
      position: { x: 0, y: 10, w: 12, h: 5 },
      query: { source: "view", viewId: POOL_VIEW_IDS.inventory, limit: 50 }
    },
    {
      id: "pools_table_liquidity",
      title: "Liquidity by pool",
      description: "Moves grouped by pool and direction \u2014 adds and removes stay separable.",
      kind: "table",
      position: { x: 0, y: 15, w: 12, h: 5 },
      query: { source: "view", viewId: POOL_VIEW_IDS.liquidity, limit: 50 }
    }
  ]
});
var POOL_PRESET_DASHBOARDS = {
  [DEX_POOLS_DASHBOARD.id]: DEX_POOLS_DASHBOARD
};

export { DEFAULT_POOL_LIMIT, DEX_POOLS_DASHBOARD, LIQUIDITY_ACTIONS, LIQUIDITY_ACTION_LABELS, MAX_FLOW_LIMIT, MIN_LIQUIDITY_SPAN_SECONDS, NATIVE_FLOW_FACETS, NATIVE_FLOW_READ_FACETS, POOL_ALERT_KINDS, POOL_ALERT_LABELS, POOL_ALERT_SEVERITY, POOL_FACETS, POOL_INVENTORY_FACETS, POOL_LIQUIDITY_FACETS, POOL_PRESET_DASHBOARDS, POOL_VIEW_IDS, TOKEN_FLOW_FACETS, TOKEN_FLOW_READ_FACETS, TRANSFER_EVENT, buildAddressLaunchesQuery, buildLaunchPoolProbeQuery, buildLpActivityQuery, buildNativeFlowQuery, buildPoolAlertsQuery, buildPoolInventoryQuery, buildPoolLaunchQuoteQuery, buildPoolLaunchTokenQuery, buildPoolLiquidityQuery, buildTokenFlowQuery, buildTokenLaunchPoolsQuery, compareLpEvents, countPools, flowForAddress, formatAmounts, formatBaseUnits, formatFeeTier, formatLegAmounts, liquidityActivityWindow, liquiditySeriesFromEvents, lpEventsFromRows, nativeFlowFromRows, netFlowByAddress, poolAlertsFromRows, poolIdentity, poolKey, poolPairLabel, poolsFromRows, tokenFlowFromRows, transferLegsFromRows };
//# sourceMappingURL=chunk-4K7AYCOR.js.map
//# sourceMappingURL=chunk-4K7AYCOR.js.map